import React, { useState, useEffect, useRef } from 'react'
import Modal from './Modal'
import { C, ui } from '../theme'

const h = ui.h
const input = ui.input
const btn = ui.btn
const btnGhost = ui.btnGhost
const row = ui.row

// DM-only campaign configuration. Holds the per-faction instant-recruitment budget (the gp cap
// for free "starting" troops), the starting-gold control, and the join/access codes. More
// settings will accrue here.
export default function CampaignSettingsModal({ factions, onSetInstantBudget, onSeedTreasury, onSetTreasury, onSeedCommanders, onClose, accessCodes, onSetCodes, joinLinkBase }) {
  // Instant budget & treasury are LIVE remaining balances (players draw the budget down; the engine
  // moves treasury daily), so we can't batch-save every row — a stale snapshot would clobber a faction
  // the DM never touched. Instead: edit freely, then one Save writes only the rows that actually differ
  // from the last-known server value (kept in a ref baseline). The factions-prop effect below re-seeds
  // untouched rows from live-sync so the baseline tracks concurrent draws.
  const roundBudget = f => Math.round(f.instant_budget_gp || 0)
  const roundTreasury = f => Math.round(f.treasury || 0)
  const [budgets, setBudgets] = useState(() => Object.fromEntries(factions.map(f => [f.id, roundBudget(f)])))
  const [treasuries, setTreasuries] = useState(() => Object.fromEntries(factions.map(f => [f.id, roundTreasury(f)])))
  const budgetBase = useRef(Object.fromEntries(factions.map(f => [f.id, roundBudget(f)])))
  const treasuryBase = useRef(Object.fromEntries(factions.map(f => [f.id, roundTreasury(f)])))
  const [budgetMsg, setBudgetMsg] = useState(null)
  const [treasuryMsg, setTreasuryMsg] = useState(null)
  const [goldMode, setGoldMode] = useState('days') // days | fixed
  const [goldDays, setGoldDays] = useState(180)
  const [cmdrsPerFaction, setCmdrsPerFaction] = useState(6)

  // Live-sync: when the factions prop changes (another actor recruited / a day advanced), pull the new
  // server value into any row the DM hasn't edited, and always advance the baseline so dirty-detection
  // compares against current truth. Edited (dirty) rows keep the DM's in-progress value.
  useEffect(() => {
    const reseed = (setVals, baseRef, valOf) => {
      setVals(prev => {
        const next = { ...prev }
        for (const f of factions) {
          const wasClean = !(f.id in prev) || Number(prev[f.id]) === Number(baseRef.current[f.id])
          if (wasClean) next[f.id] = valOf(f)
        }
        return next
      })
      baseRef.current = Object.fromEntries(factions.map(f => [f.id, valOf(f)]))
    }
    reseed(setBudgets, budgetBase, roundBudget)
    reseed(setTreasuries, treasuryBase, roundTreasury)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factions])

  // Join/access codes — seeded from the DM-only fetch when it arrives.
  const [dmCode, setDmCode] = useState('')
  const [facCodes, setFacCodes] = useState({})
  const [codesMsg, setCodesMsg] = useState(null)
  const [copied, setCopied] = useState(null)
  useEffect(() => {
    if (!accessCodes) return
    setDmCode(accessCodes.dmCode || '')
    setFacCodes(Object.fromEntries((accessCodes.factions || []).map(f => [f.id, f.code || ''])))
  }, [accessCodes])
  const saveCodes = async () => {
    setCodesMsg(null)
    const body = { dmCode, factionCodes: (accessCodes?.factions || []).map(f => ({ factionId: f.id, code: facCodes[f.id] || '' })) }
    const res = await onSetCodes(body)
    if (res && !res.ok) setCodesMsg(res.data.error || 'failed to save codes')
    else { setCodesMsg('Saved ✓'); setTimeout(() => setCodesMsg(m => (m === 'Saved ✓' ? null : m)), 1400) }
  }
  const copyLink = (code, key) => {
    if (!code) return
    const link = `${joinLinkBase}${encodeURIComponent(code)}`
    try { navigator.clipboard.writeText(link); setCopied(key); setTimeout(() => setCopied(k => (k === key ? null : k)), 1200) } catch { /* clipboard unavailable */ }
  }

  // Save only the rows whose edited value differs from the last-known server value — untouched
  // factions are never written, so a concurrent draw on a faction the DM didn't edit can't be stomped.
  const budgetDirty = f => Number(budgets[f.id]) !== Number(budgetBase.current[f.id])
  const treasuryDirty = f => Number(treasuries[f.id]) !== Number(treasuryBase.current[f.id])

  const flash = (setMsg) => { setMsg('Saved ✓'); setTimeout(() => setMsg(m => (m === 'Saved ✓' ? null : m)), 1400) }
  const saveBudgets = async () => {
    const dirty = factions.filter(budgetDirty)
    if (!dirty.length) return
    let ok = true
    for (const f of dirty) { const res = await onSetInstantBudget(f.id, Number(budgets[f.id]) || 0); if (res && !res.ok) ok = false }
    if (ok) flash(setBudgetMsg)
  }
  const saveTreasuries = async () => {
    const dirty = factions.filter(treasuryDirty)
    if (!dirty.length) return
    let ok = true
    for (const f of dirty) { const res = await onSetTreasury(f.id, Number(treasuries[f.id]) || 0); if (res && !res.ok) ok = false }
    if (ok) flash(setTreasuryMsg)
  }

  return (
    <Modal title="Campaign settings" onClose={onClose} width={480}>
      <div style={h}>Access codes</div>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
        People join by entering a code (or opening its link). Pick memorable codes and share the link with each ally.
        Editing a code changes it for everyone; blank unclaims that slot.
      </div>
      {!accessCodes ? (
        <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 8 }}>Loading codes…</div>
      ) : (
        <>
          <div style={{ ...row, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>DM</span>
            <input style={{ ...input, width: 150, marginBottom: 0 }} placeholder="unset" value={dmCode}
              onChange={e => setDmCode(e.target.value)} />
            <button style={btnGhost} onClick={() => copyLink(dmCode, 'dm')} disabled={!dmCode.trim()}>{copied === 'dm' ? 'Copied ✓' : 'Copy link'}</button>
          </div>
          {(accessCodes.factions || []).map(f => (
            <div key={f.id} style={{ ...row, alignItems: 'center', marginBottom: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: f.color || C.inkFaint, display: 'inline-block' }} />
              <span style={{ flex: 1, fontSize: 13 }}>{f.name}</span>
              <input style={{ ...input, width: 150, marginBottom: 0 }} placeholder="unclaimed" value={facCodes[f.id] ?? ''}
                onChange={e => setFacCodes(c => ({ ...c, [f.id]: e.target.value }))} />
              <button style={btnGhost} onClick={() => copyLink(facCodes[f.id], `f${f.id}`)} disabled={!(facCodes[f.id] || '').trim()}>{copied === `f${f.id}` ? 'Copied ✓' : 'Copy link'}</button>
            </div>
          ))}
          <div style={{ ...row, alignItems: 'center', marginTop: 4 }}>
            <button style={btn} onClick={saveCodes}>Save codes</button>
            {codesMsg && <span style={{ fontSize: 12, color: codesMsg === 'Saved ✓' ? undefined : C.crimson, opacity: 0.85 }}>{codesMsg}</span>}
          </div>
        </>
      )}

      <div style={h}>Instant-recruitment budget</div>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
        The gp each faction may spend on free “starting / instant” troops. Players draw it down as they raise starting forces.
      </div>
      {factions.map(f => (
        <div key={f.id} style={{ ...row, alignItems: 'center', marginBottom: 6 }}>
          <span style={{ flex: 1, fontSize: 13 }}>{f.name}{budgetDirty(f) && <span style={{ color: C.gold, marginLeft: 6 }} title="edited — unsaved">•</span>}</span>
          <input style={{ ...input, width: 130, marginBottom: 0, textAlign: 'right', ...(budgetDirty(f) ? { borderColor: C.gold } : {}) }} type="number" min={0}
            value={budgets[f.id] ?? 0}
            onChange={e => setBudgets(b => ({ ...b, [f.id]: e.target.value === '' ? '' : Math.max(0, Number(e.target.value)) }))} />
        </div>
      ))}
      <div style={{ ...row, alignItems: 'center', marginTop: 4 }}>
        <button style={{ ...btn, ...(factions.some(budgetDirty) ? {} : { opacity: 0.5, cursor: 'default' }) }}
          disabled={!factions.some(budgetDirty)} onClick={saveBudgets}>Save budgets</button>
        {budgetMsg && <span style={{ fontSize: 12, opacity: 0.85 }}>{budgetMsg}</span>}
      </div>

      {onSeedCommanders && (
        <>
          <div style={h}>Starting commanders</div>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
            Top every faction up to N living commanders (the Rules suggest 6). Seeded commanders are blank —
            players name them and spend their points in the Commanders tab.
          </div>
          <div style={row}>
            <input style={{ ...input, flex: 1, marginBottom: 0 }} type="number" min={0} max={20}
              value={cmdrsPerFaction} onChange={e => setCmdrsPerFaction(Number(e.target.value))} />
            <button style={btn} onClick={() => onSeedCommanders(cmdrsPerFaction)}>Seed {cmdrsPerFaction}/faction</button>
          </div>
        </>
      )}

      <div style={h}>Starting gold</div>
      <div style={{ ...row, marginBottom: 8 }}>
        <button style={{ flex: 1, ...(goldMode === 'days' ? btn : btnGhost) }} onClick={() => setGoldMode('days')}>Days of revenue</button>
        <button style={{ flex: 1, ...(goldMode === 'fixed' ? btn : btnGhost) }} onClick={() => setGoldMode('fixed')}>Fixed per society</button>
      </div>

      {goldMode === 'days' ? (
        <>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Set every faction’s treasury to N days of its own tax revenue.</div>
          <div style={row}>
            <input style={{ ...input, flex: 1, marginBottom: 0 }} type="number" value={goldDays} onChange={e => setGoldDays(Number(e.target.value))} />
            <button style={btn} onClick={() => onSeedTreasury(goldDays)}>Seed {goldDays}d</button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Set each faction’s treasury to a fixed gp amount.</div>
          {factions.map(f => (
            <div key={f.id} style={{ ...row, alignItems: 'center', marginBottom: 6 }}>
              <span style={{ flex: 1, fontSize: 13 }}>{f.name}{treasuryDirty(f) && <span style={{ color: C.gold, marginLeft: 6 }} title="edited — unsaved">•</span>}</span>
              <input style={{ ...input, width: 130, marginBottom: 0, textAlign: 'right', ...(treasuryDirty(f) ? { borderColor: C.gold } : {}) }} type="number" min={0}
                value={treasuries[f.id] ?? 0}
                onChange={e => setTreasuries(t => ({ ...t, [f.id]: e.target.value === '' ? '' : Math.max(0, Number(e.target.value)) }))} />
            </div>
          ))}
          <div style={{ ...row, alignItems: 'center', marginTop: 4 }}>
            <button style={{ ...btn, ...(factions.some(treasuryDirty) ? {} : { opacity: 0.5, cursor: 'default' }) }}
              disabled={!factions.some(treasuryDirty)} onClick={saveTreasuries}>Save treasuries</button>
            {treasuryMsg && <span style={{ fontSize: 12, opacity: 0.85 }}>{treasuryMsg}</span>}
          </div>
        </>
      )}
    </Modal>
  )
}
